import { useCallback, useEffect, useRef, useState } from 'react'
import BaseReactPlayer, { BaseReactPlayerProps } from 'react-player/base'

import { usePlayingResource } from '@/core/resources'

type SoundPlayerState = {
  isUriUnavailable: boolean
  duration: number
  isPlaying: boolean
  progress: number
  isSeeking: boolean
  valueWhileSeeking: number
}

/**
 * 音声プレイヤーを扱うためのカスタムフック

  @param {string} uri 再生したいリソースのURL

  @description
  フックの戻り値をReactPlayerコンポーネントおよびSoundPlayerControlに渡すことでオーディオ再生を行うことができる。
 *
 * -- ReactPlayerに注入する --
 * @returns player uriの音声を再生するaudioタグのref値
 * @returns playing プレイヤーが現在再生しているかどうか。SoundPlayerControlにも使用する
 * @returns duration 音声リソースの再生時間
 * @returns isUriUnavailable エラーでリソースが再生できない場合true
 * @returns sliderValue スライダーUIの現在の再生位置
 * @returns handleDuration リソースの再生時間を取得する際のコールバック。再生時間をプレイヤーに表示するのに用いる。
 * @returns handleProgress 再生が進む毎に呼び出される処理
 * @returns handleError エラーが起きた際の処理
 *
 * -- SoundPlayerControlに注入する --
 * @returns play 音声を再生するメソッド
 * @returns pause 音声を停止するメソッド
 * @returns change スライダーのホイールを動かしているときの処理
 * @returns commitChange スライダーのホイールを離したときの処理
 * @returns rewind 巻き戻しボタンを押した時の処理
 * @returns forward 早送りボタンを押した時の処理
 */

export function useSoundPlayer(uri: string) {
  const { playingResource, playResource, pauseResource } = usePlayingResource()

  const player = useRef<BaseReactPlayer<BaseReactPlayerProps>>(null)

  const [state, setState] = useState<SoundPlayerState>({
    isUriUnavailable: false,
    duration: 0,
    isPlaying: false,
    progress: 0,
    isSeeking: false,
    valueWhileSeeking: 0,
  })

  const mergeState = (obj: Partial<SoundPlayerState>) =>
    setState(state => ({ ...state, ...obj }))

  useEffect(() => {
    player.current?.getInternalPlayer()?.load()
    mergeState({ duration: player.current?.getDuration() })

    mergeState({
      isUriUnavailable: false,
    })
  }, [player])

  return {
    player,
    playing: state.isPlaying && playingResource === uri,
    duration: state.duration,
    isUriUnavailable: state.isUriUnavailable,
    sliderValue: state.isSeeking ? state.valueWhileSeeking : state.progress,

    handleDuration: useCallback((duration: number) => {
      mergeState({ duration })
    }, []),

    handleProgress: useCallback(
      ({ playedSeconds: progress }: { playedSeconds: number }) => {
        mergeState({ progress })
      },
      []
    ),

    handleError: useCallback(() => {
      // const mediaError = err?.target?.error
      mergeState({ isUriUnavailable: true })
    }, []),

    play: useCallback(() => {
      setState(state => ({ ...state, isPlaying: true }))
      playResource(uri)
    }, [playResource, uri]),

    pause: useCallback(() => {
      setState(state => ({ ...state, isPlaying: false }))
      pauseResource()
    }, [pauseResource]),

    change: useCallback((_: unknown, value: number | number[]) => {
      mergeState({ isSeeking: true, valueWhileSeeking: value as number })
    }, []),

    commitChange: useCallback((_: unknown, value: number | number[]) => {
      mergeState({
        progress: value as number,
        valueWhileSeeking: 0,
        isSeeking: false,
      })
      player.current?.seekTo(value as number, 'seconds')
    }, []),

    rewind: useCallback(() => {
      if (player && player.current) {
        const newTime = Math.max(player.current?.getCurrentTime() - 10, 0)
        mergeState({ progress: newTime })
        player.current?.seekTo(newTime, 'seconds')
      }
    }, []),

    forward: useCallback(() => {
      if (player && player.current) {
        const newTime = Math.min(
          player.current?.getCurrentTime() + 10,
          player.current?.getDuration()
        )

        mergeState({ progress: newTime })
        player.current?.seekTo(newTime, 'seconds')
      }
    }, []),

    end: useCallback(() => {
      player.current?.seekTo(0, 'seconds')
      mergeState({ progress: 0, isPlaying: false })
      pauseResource()
    }, [pauseResource]),
  }
}
